





































import gql from 'graphql-tag';
import Vue from 'vue';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import LoadingSpinner from '@reciprocity/loading-spinner';
import infiniteScroll from 'vue-infinite-scroll';
import DashboardDrilldownRow from './DashboardDrilldownRow';
import { ModalSize } from '@/components/zen_modal/types';
import { MetricOptions, DimensionOptions } from '@/constants/DashboardConstants';
import { mapFilters } from '@/utils/DashboardUtils';
import { CustomAttributeServerType } from '../../../pages/settings/custom_attributes/types';

const PAGE_SIZE = 10;
const EXCLUDE_COLUMNS = ['description', 'testPlan'];
const RISK_DEFAULT_COLUMNS = ['title', 'ownersGroup', 'status'];

export default Vue.extend({
  name: 'DashboardDrilldownCard',
  components: {
    DashboardDrilldownRow,
    LoadingSpinner,
  },
  directives: {
    infiniteScroll,
  },
  props: {
    metric: {
      type: String,
      required: true,
    },
    dimension: {
      type: String,
      default: () => null,
    },
    mappedObject: {
      type: String,
      default: () => null,
    },
    mappedObjectAttr: {
      type: String,
      default: () => null,
    },
    customAttrId: {
      type: String,
      default: () => null,
    },
    customAttrType: {
      type: String,
      default: () => null,
    },
    dimensionItem: {
      type: String,
      required: true,
    },
    dimensionItemId: {
      type: Number,
      default: () => null,
    },
    title: {
      type: String,
      default: () => 'Drilldown',
    },
    filterByObject: {
      type: Object,
      default: null,
    },
    filters: {
      type: Array,
      default: () => [],
    },
  },
  apollo: {
    items: {
      query: gql`
        query GetDrilldownItems(
          $metric: DashboardMetric!
          $dimension: DashboardDimension
          $mappedObject: DashboardMetric
          $mappedObjectAttr: DashboardDimension
          $customAttrId: String
          $customAttrType: String
          $filter: ID
          $filters: [DashboardFilter!]
          $limit: Int!
          $offset: Int!
        ) {
          dashboardDrilldown(
            metric: $metric
            dimension: $dimension
            mappedObject: $mappedObject
            mappedObjectAttr: $mappedObjectAttr
            customAttrId: $customAttrId
            customAttrType: $customAttrType
            filter: $filter
            filters: $filters
            limit: $limit
            offset: $offset
          ) {
            data {
              id
              mappedId
              attributeValue
            }
          }
        }
      `,
      variables() {
        const {
          metric,
          dimension,
          mappedObject,
          mappedObjectAttr,
          dimensionItem,
          dimensionItemId,
          customAttrId,
          customAttrType,
          filters,
        } = this;

        const NOT_SET = 'Not set';
        const options = get(DimensionOptions, dimension, {});
        const empty = get(options, 'emptyValue', null);

        const mappedValue = options.labelMappings
          ? get(options, `labelMappings.${dimensionItem}`, dimensionItem)
          : dimensionItem;

        const value = mappedValue !== NOT_SET ? mappedValue : empty;

        const drilldownFilters = [...mapFilters(filters)];
        const filterValuesAndOperator = {
          values: [dimensionItemId && !this.shouldQueryByValue(customAttrType) ? dimensionItemId : value],
          operator: 'AND',
        };

        if (mappedObjectAttr) {
          drilldownFilters.push({
            type: 'MAPPING_ATTR',
            attribute: mappedObjectAttr,
            customAttrId,
            customAttrType,
            object: mappedObject,
            ...filterValuesAndOperator,
          });
        } else {
          drilldownFilters.push({
            type: 'ATTRIBUTE',
            attribute: dimension,
            customAttrId,
            customAttrType,
            ...filterValuesAndOperator,
          });
        }

        return {
          metric,
          dimension: dimension || null,
          mappedObject: mappedObject || null,
          mappedObjectAttr: mappedObjectAttr || null,
          customAttrId: customAttrId || null,
          customAttrType: customAttrType || null,
          filters: drilldownFilters,
          limit: PAGE_SIZE,
          offset: 0,
        };
      },
      update(result) {
        if (window.analytics) {
          analytics.track('dashboard_drilldown_shown', {
            metric: this.metric,
            dimension: this.dimension,
            mapped_object: this.mappedObject,
            mapped_object_attr: this.mappedObjectAttr,
            cad_type: this.customAttrType,
          });
        }
        const data = get(result, 'dashboardDrilldown.data', []);

        if (data.length < PAGE_SIZE) {
          this.hasNextPage = false;
        }

        return data.map(item => {
          const dbId = get(item, 'id');
          const id = `${this.model}:${dbId}`;
          return {
            id,
            dbId,
            type: this.model,
          };
        });
      },
    },
    columns: {
      query: gql`
        query GetDrilldownColumns($name: SearchType!) {
          objectType(name: $name) {
            attrs(context: SOR_LISTING) {
              name
              displayName
              default: sorListingDefault
              __typename
            }
          }
        }
      `,
      variables() {
        return {
          name: this.model,
        };
      },
      update(result) {
        return get(result, 'objectType.attrs', [])
          .filter(attribute => attribute.default)
          .filter(attribute => !EXCLUDE_COLUMNS.includes(attribute.name))
          .filter(attribute => (this.metric === 'RISK' ? RISK_DEFAULT_COLUMNS.includes(attribute.name) : true));
      },
    },
  },
  data() {
    return {
      items: [],
      relevantIds: [],
      columns: [],
      count: null,
      rowComponent: DashboardDrilldownRow,
      page: 0,
      hasNextPage: true,
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
    modalTitle() {
      return this.mappedObject === null
        ? `${this.title} - ${this.dimensionItem}`
        : `${this.title} - ${MetricOptions[this.mappedObject].label}  ${this.dimensionItem}`;
    },
    model() {
      const { metric } = this;
      return get(MetricOptions, `${metric}.model`);
    },
    columnsNames() {
      return this.columns.map(column => column.name);
    },
  },
  created() {
    this.changeModalSize(ModalSize.Large);
  },
  methods: {
    ...mapActions({
      changeModalSize: 'modals/changeSize',
    }),
    onLoadMore() {
      if (!this.hasNextPage || this.$apollo.loading) return;

      this.page++;

      this.$apollo.queries.items.fetchMore({
        variables: {
          offset: this.page * PAGE_SIZE,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const data = get(previousResult, 'dashboardDrilldown.data', []);
          const newData = get(fetchMoreResult, 'dashboardDrilldown.data', []);

          if (!newData.length) {
            this.hasNextPage = false;
          }

          return {
            dashboardDrilldown: {
              __typename: 'DashboardDrilldown',
              data: [...data, ...newData],
            },
          };
        },
      });
    },
    shouldQueryByValue(customAttrType: string): Boolean {
      return (
        customAttrType == CustomAttributeServerType.DROPDOWN ||
        customAttrType == CustomAttributeServerType.MULTI ||
        customAttrType == CustomAttributeServerType.TEXT ||
        customAttrType == CustomAttributeServerType.CHECKBOX ||
        customAttrType == CustomAttributeServerType.DATE ||
        customAttrType == CustomAttributeServerType.FLOAT ||
        customAttrType == CustomAttributeServerType.INT
      );
    },
  },
});
